import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/AdminNavigation';
import NavigationBreadcrumbs from '../../components/NavigationBreadcrumbs';
import { useAuth } from '../../components/AuthenticationBoundary';
import { supabase } from '../../lib/supabaseClient';

import Button from '../../components/ui/Button';
import OrdersTable from './components/OrdersTable';
import OrdersTableMobile from './components/OrdersTableMobile';
import FilterControls from './components/FilterControls';
import BulkActions from './components/BulkActions';
import OrderDetailsModal from './components/OrderDetailsModal';
import StatusUpdateModal from './components/StatusUpdateModal';
import EmptyState from './components/EmptyState';

 const AdminOrdersManagement = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    serviceType: 'all',
    startDate: '',
    endDate: '',
    searchQuery: ''
  });

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState('');

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  }, [totalCount]);

  const fetchOrders = useCallback(async () => {
    setIsFetching(true);
    setFetchError('');

    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const orderItemsSelect =
        filters?.serviceType && filters.serviceType !== 'all'
          ? 'order_items!inner ( id, brand, model, service_type, price, memo )'
          : 'order_items ( id, brand, model, service_type, price, memo )';

      let query = supabase
        .from('orders')
        .select(
          `id,
           order_number,
           customer_name,
           phone,
           address,
           status,
           order_date,
           total_amount,
           payment_status,
           pickup_type,
           custom_instructions,
           internal_notes,
           kakao_notification,
           ${orderItemsSelect}`,
          { count: 'exact' }
        )
        .order('order_date', { ascending: false })
        .range(from, to);

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.serviceType && filters.serviceType !== 'all') {
        query = query.eq('order_items.service_type', filters.serviceType);
      }

      if (filters?.startDate) {
        query = query.gte('order_date', new Date(filters.startDate).toISOString());
      }

      if (filters?.endDate) {
        const end = new Date(filters.endDate);
        end.setHours(23, 59, 59, 999);
        query = query.lte('order_date', end.toISOString());
      }

      if (filters?.searchQuery) {
        const q = filters.searchQuery.trim();
        if (q) {
          query = query.or(
            `customer_name.ilike.%${q}%,phone.ilike.%${q}%,order_number.ilike.%${q}%`
          );
        }
      }

      const { data, error, count } = await query;
      if (error) {
        throw error;
      }

      const mapped = (data || []).map((row) => {
        const items = (row.order_items || []).map((it) => ({
          brand: it.brand,
          model: it.model,
          serviceType: it.service_type,
          price: it.price,
          memo: it.memo,
        }));

        return {
          id: row.id,
          orderNumber: row.order_number,
          customerName: row.customer_name,
          phone: row.phone,
          address: row.address,
          status: row.status,
          orderDate: row.order_date,
          totalAmount: row.total_amount,
          paymentStatus: row.payment_status,
          pickupType: row.pickup_type,
          customInstructions: row.custom_instructions,
          internalNotes: row.internal_notes,
          kakaoNotification: !!row.kakao_notification,
          items,
          itemsCount: items.length,
        };
      });

      setOrders(mapped);
      setTotalCount(count || 0);
    } catch (e) {
      setOrders([]);
      setTotalCount(0);
      setFetchError(e?.message || '주문 목록을 불러오지 못했습니다.');
    } finally {
      setIsFetching(false);
    }
  }, [filters, page]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin-login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let mounted = true;

    const run = async () => {
      setServicesLoading(true);
      setServicesError('');

      try {
        const { data, error } = await supabase
          .from('services')
          .select('id, name, active, created_at')
          .eq('active', true)
          .order('created_at', { ascending: true });

        if (error) throw error;
        if (!mounted) return;
        setServices(data || []);
      } catch (e) {
        if (!mounted) return;
        setServices([]);
        setServicesError(e?.message || '서비스 목록을 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setServicesLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchOrders();
  }, [fetchOrders, isAuthenticated]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setSelectedOrders([]);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setPage(1);
    setSelectedOrders([]);
    setFilters({
      status: 'all',
      serviceType: 'all',
      startDate: '',
      endDate: '',
      searchQuery: ''
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin-login');
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev?.includes(orderId)
        ? prev?.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders?.length === orders?.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders?.map(order => order?.id));
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (updateData) => {
    if (!updateData?.orderId || !updateData?.status) return;

    console.log('[StatusUpdate] 요청:', {
      orderId: updateData?.orderId,
      status: updateData?.status,
      sendNotification: updateData?.sendNotification,
    });

    await supabase
      .from('orders')
      .update({
        status: updateData.status,
        internal_notes: updateData?.notes || undefined,
      })
      .eq('id', updateData.orderId);

    if (updateData?.sendNotification && updateData.status === '주문완료') {
      try {
        await supabase.functions.invoke('send-order-completed', {
          body: { order_id: updateData.orderId },
        });
      } catch (notifyError) {
        console.warn('주문완료 알림톡 발송 실패:', notifyError);
      }
    }

    if (updateData?.sendNotification && updateData.status === '픽업 예정') {
      try {
        await supabase.functions.invoke('send-pickup-guide', {
          body: { order_id: updateData.orderId },
        });
      } catch (notifyError) {
        console.warn('픽업 예정 알림톡 발송 실패:', notifyError);
      }

      try {
        await supabase.functions.invoke('enqueue-notification', {
          body: { order_id: updateData.orderId, template_key: 'PICKUP_SCHEDULED' },
        });
      } catch (notifyError) {
        console.warn('픽업 예정 예약 알림 enqueue 실패:', notifyError);
      }
    }

    if (updateData?.sendNotification && updateData.status === '세탁 중') {
      try {
        await supabase.functions.invoke('send-pickup-en-route', {
          body: { order_id: updateData.orderId },
        });
      } catch (notifyError) {
        console.warn('세탁 중 알림톡 발송 실패:', notifyError);
      }
    }

    if (updateData?.sendNotification && updateData.status === '배송 예정') {
      try {
        await supabase.functions.invoke('send-delivery-scheduled', {
          body: { order_id: updateData.orderId },
        });
      } catch (notifyError) {
        console.warn('배송 예정 알림톡 발송 실패:', notifyError);
      }
    }

    if (updateData?.sendNotification && updateData.status === '완료') {
      try {
        console.log('[StatusUpdate] 완료 알림톡 invoke 시작:', { order_id: updateData.orderId });
        const { data, error } = await supabase.functions.invoke('send-delivery-completed', {
          body: { order_id: updateData.orderId },
        });

        if (error) {
          console.error('[StatusUpdate] 완료 알림톡 invoke error:', {
            name: error?.name,
            message: error?.message,
            context: error?.context,
            cause: error?.cause,
            raw: error,
          });
        }

        console.log('[StatusUpdate] 완료 알림톡 invoke 결과:', data);
      } catch (notifyError) {
        console.error('[StatusUpdate] 완료 알림톡 발송 실패(throw):', {
          name: notifyError?.name,
          message: notifyError?.message,
          cause: notifyError?.cause,
          raw: notifyError,
        });
      }
    }

    setSelectedOrders([]);
    fetchOrders();
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (!newStatus || selectedOrders.length === 0) return;
    await supabase.from('orders').update({ status: newStatus }).in('id', selectedOrders);
    setSelectedOrders([]);
    fetchOrders();
  };

  const handleBulkDelete = async () => {
    if (!selectedOrders?.length) return;

    const ok = window.confirm(`선택한 ${selectedOrders.length}개 주문을 삭제하시겠습니까?`);
    if (!ok) return;

    const { error } = await supabase.from('orders').delete().in('id', selectedOrders);
    if (error) {
      window.alert(error?.message || '삭제에 실패했습니다.');
      return;
    }

    setSelectedOrders([]);
    fetchOrders();
  };

  const handleExport = () => {
    const selectedOrdersData = orders?.filter(order => selectedOrders?.includes(order?.id));
    const csvContent = "data:text/csv;charset=utf-8," +"주문번호,고객명,연락처,주소,상태,주문일,금액\n"
      + selectedOrdersData?.map(order => 
          `${order?.orderNumber},${order?.customerName},${order?.phone},${order?.address},${order?.status},${order?.orderDate},${order?.totalAmount}`
        )?.join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link?.setAttribute("href", encodedUri);
    link?.setAttribute("download", `orders_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <NavigationBreadcrumbs />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-foreground">주문 관리</h1>
            <p className="mt-1 text-sm text-muted-foreground text-white">
              전체 {totalCount}개 주문 중 {orders?.length}개 표시
            </p>
          </div>
          <Button
            variant="outline"
            className="bg-white text-black hover:bg-white/90"
            iconName="LogOut"
            iconPosition="left"
            onClick={handleLogout}
          >
            로그아웃
          </Button>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg bg-card p-6 shadow-card">
            <FilterControls
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              services={services}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            />
          </div>

          {selectedOrders?.length > 0 && (
            <BulkActions
              selectedCount={selectedOrders?.length}
              onBulkStatusUpdate={handleBulkStatusUpdate}
              onExport={handleExport}
              onBulkDelete={handleBulkDelete}
            />
          )}

          <div className="rounded-lg bg-card shadow-card">
            {isFetching ? (
              <div className="p-12 text-center">
                <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="text-sm text-muted-foreground">주문 목록을 불러오는 중...</p>
              </div>
            ) : fetchError ? (
              <div className="p-6">
                <div className="rounded-lg bg-error/10 p-4">
                  <p className="text-sm text-error">{fetchError}</p>
                </div>
              </div>
            ) : orders?.length === 0 ? (
              <div className="p-6">
                <EmptyState onResetFilters={handleResetFilters} />
              </div>
            ) : (
              <>
                {isMobile ? (
                  <div className="p-4">
                    <OrdersTableMobile
                      orders={orders}
                      onViewDetails={handleViewDetails}
                      onUpdateStatus={handleUpdateStatus}
                      selectedOrders={selectedOrders}
                      onSelectOrder={handleSelectOrder}
                    />
                  </div>
                ) : (
                  <OrdersTable
                    orders={orders}
                    onViewDetails={handleViewDetails}
                    onUpdateStatus={handleUpdateStatus}
                    selectedOrders={selectedOrders}
                    onSelectOrder={handleSelectOrder}
                    onSelectAll={handleSelectAll}
                  />
                )}

                <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    총 <span className="font-medium text-foreground">{totalCount}</span>개
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <span className="text-sm text-muted-foreground">
                      페이지 <span className="font-medium text-foreground">{page}</span> / {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedOrders([]);
                          setPage((p) => Math.max(1, p - 1));
                        }}
                        disabled={page <= 1}
                      >
                        이전
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedOrders([]);
                          setPage((p) => Math.min(totalPages, p + 1));
                        }}
                        disabled={page >= totalPages}
                      >
                        다음
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      {showDetailsModal && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrder(null);
          }}
        />
      )}
      {showStatusModal && (
        <StatusUpdateModal
          order={selectedOrder}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedOrder(null);
          }}
          onUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default AdminOrdersManagement;